import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Route } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHead, Section, Reveal, CtaBand, QaBlock } from "@/components/Shared";
import { UI, PATHS } from "@/i18n/ui";
import { PROGRAMS, programCommon, findProgram } from "@/data/programs";

export function ProgramsPage({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const meta = {
    id: { title: "Program Kesehatan Mental Terstruktur di Bali | Bali Empower", desc: "Program psikologi terstruktur Bali Empower di Denpasar, Bali: manajemen stres & burnout, pendampingan anak & remaja, dan pengembangan diri. Diawali konsultasi untuk memastikan kesesuaian." },
    en: { title: "Structured Mental Health Programs in Bali | Bali Empower", desc: "Bali Empower's structured psychology programs in Denpasar, Bali: stress & burnout management, child & adolescent support, and personal development. Each begins with a consultation." },
  }[lang];
  return (
    <div data-testid="programs-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.programs} title={t.nav.programs}
        sub={lang === "id"
          ? "Program terstruktur dirancang untuk kebutuhan yang memerlukan pendampingan bertahap dan terukur, lebih dari sekadar sesi konseling tunggal. Setiap program diawali konsultasi."
          : "Structured programs are designed for needs that require staged, measurable support beyond single counseling sessions. Every program begins with a consultation."} />
      <Section className="pt-0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROGRAMS.map((pr, i) => (
            <Reveal key={pr.slug.en} delay={i * 0.05}>
              <Link to={p.program(pr.slug[lang])} data-testid={`program-card-${pr.slug.en}`}
                className="be-card block h-full rounded-2xl border border-[#E5E1D8] bg-white p-8">
                <Route size={24} className="text-[#4A6B53]" strokeWidth={1.6} />
                <h2 className="mt-4 text-lg font-display text-[#1F2937]">{pr.name[lang]}</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">{pr.problem[lang]}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                  {t.cta.learnMore} <ArrowRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <p className="mt-10 text-sm italic text-[#4B5563] max-w-2xl">{programCommon.note[lang]}</p>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

export function ProgramDetail({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const { slug } = useParams();
  const pr = findProgram(lang, slug);
  if (!pr) return <Navigate to={p.programs} replace />;
  const meta = {
    id: { title: `${pr.name.id} | Bali Empower Denpasar`, desc: `${pr.problem.id} Program terstruktur oleh Bali Empower di Denpasar, Bali.` },
    en: { title: `${pr.name.en} | Bali Empower Denpasar`, desc: `${pr.problem.en} A structured program by Bali Empower in Denpasar, Bali.` },
  }[lang];
  return (
    <div data-testid={`program-detail-${pr.slug.en}`}>
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.programs} title={pr.name[lang]} sub={pr.problem[lang]} />
      <Section className="pt-0">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <QaBlock q={t.misc.forWho}><p>{pr.for[lang]}</p></QaBlock>
            <QaBlock q={t.misc.goals}>
              <ul className="list-disc pl-5 space-y-1.5">
                {pr.goals[lang].map((g) => <li key={g}>{g}</li>)}
              </ul>
            </QaBlock>
            <QaBlock q={t.misc.structure}><p>{programCommon.structure[lang]}</p></QaBlock>
            <QaBlock q={t.misc.duration}><p>{programCommon.duration[lang]}</p></QaBlock>
            <QaBlock q={t.misc.cost}><p>{programCommon.price[lang]}</p></QaBlock>
            <QaBlock q={t.misc.provider}><p>{programCommon.professional[lang]}</p></QaBlock>
            <p className="mt-8 text-sm italic text-[#4B5563]">{programCommon.note[lang]}</p>
          </div>
          <aside>
            <div className="rounded-2xl border border-[#E5E1D8] bg-white p-8 sticky top-24">
              <h2 className="font-display text-xl text-[#1F2937]">{t.cta.book}</h2>
              <Link to={p.booking} data-testid="program-book-btn"
                className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white font-medium px-6 py-3.5 hover:bg-[#3A5542] transition-colors">
                {t.cta.book} <ArrowRight size={15} />
              </Link>
            </div>
          </aside>
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
